import { useState } from "react";
import { motion } from "framer-motion";
import { Volume2, Volume1, VolumeX, Play, X } from "lucide-react";
import { asset } from "@/lib/api";

export const fmt = (ms) => { const s = Math.floor((ms || 0) / 1000); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };

export const SECTION_LABEL = { works: "Works", comics: "Comics", timelapse: "Timelapses", webtoons: "Webtoons" };

const Range = ({ testid, label, value, max, onInput, onCommit, step }) => (
  <input type="range" data-testid={testid} aria-label={label} min={0} max={max || 1} step={step} value={Math.min(value, max || 1)}
    onChange={(e) => onInput(Number(e.target.value))}
    onPointerUp={(e) => onCommit?.(Number(e.currentTarget.value))}
    onKeyUp={(e) => onCommit?.(Number(e.currentTarget.value))}
    onTouchEnd={(e) => onCommit?.(Number(e.currentTarget.value))}
    style={{ "--pct": `${max ? (Math.min(value, max) / max) * 100 : 0}%` }}
    className="music-range w-full" />
);

export const SeekBar = ({ s, fallbackMs }) => {
  const [drag, setDrag] = useState(null);
  const duration = s.pb.duration || fallbackMs || 0;
  const pos = drag ?? s.pb.position;
  return (
    <div>
      <Range testid="music-seek" label="Seek" value={pos} max={duration} step={250} onInput={setDrag}
        onCommit={(v) => { s.seek(v); setDrag(null); }} />
      <div className="flex justify-between mt-1 font-mono text-[10px] text-zinc-400">
        <span data-testid="music-position">{fmt(pos)}</span><span data-testid="music-duration">{fmt(duration)}</span>
      </div>
    </div>
  );
};

export const MuteBtn = ({ s, testid, size = 16 }) => {
  const Icon = s.muted || s.volume === 0 ? VolumeX : s.volume < 0.5 ? Volume1 : Volume2;
  return (
    <button type="button" data-testid={testid} onClick={s.toggleMute} aria-label={s.muted ? "Unmute" : "Mute"} aria-pressed={s.muted}
      className={`w-9 h-9 grid place-items-center transition-colors hover:bg-[#F5A524]/15 ${s.muted ? "text-[#FF2A55]" : "text-[#F5A524]"}`}>
      <Icon size={size} />
    </button>
  );
};

export const VolumeControl = ({ s }) => (
  <div className="flex items-center gap-2 px-4 pb-3" data-testid="music-volume-control">
    <MuteBtn s={s} testid="music-mute-btn" />
    <Range testid="music-volume" label="Volume" value={s.muted ? 0 : s.volume} max={1} step={0.01} onInput={s.setVolume} />
    <span className="w-8 text-right font-mono text-[10px] text-zinc-400">{s.muted ? "MUTE" : Math.round(s.volume * 100)}</span>
  </div>
);

export const SectionPrompt = ({ name, track, onPlay, onDismiss }) => (
  <motion.div data-testid="music-section-prompt" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
      className="mb-2 clip-tab bg-[#F5A524] text-black flex items-center gap-2 pr-1 w-[min(320px,calc(100vw-24px))]">
      <button type="button" data-testid="music-section-prompt-play" onClick={onPlay} className="flex items-center gap-3 min-w-0 flex-1 text-left p-1.5">
        <span className="relative w-10 h-10 shrink-0 bg-black">
          {track.cover && <img src={asset(track.cover)} alt="" className="w-full h-full object-cover" />}
          <span className="absolute inset-0 grid place-items-center bg-black/40 text-[#F5A524]"><Play size={16} /></span>
        </span>
        <span className="min-w-0">
          <span className="block font-mono text-[10px] font-bold tracking-[0.2em]">TAP TO PLAY · {SECTION_LABEL[name]?.toUpperCase()} SOUNDTRACK</span>
          <span className="block text-sm font-semibold truncate">{track.title}</span>
        </span>
      </button>
      <button type="button" data-testid="music-section-prompt-dismiss" onClick={onDismiss} aria-label="Dismiss" className="w-8 h-8 grid place-items-center hover:bg-black/10"><X size={14} /></button>
    </motion.div>
);
